import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import SignInContainer from './components/sign-in-container'
import SignInFormContainer from './components/sign-in-form-container'
import FormInputPassword from './components/form-input-password'
import SignInSubmitButton from './components/sign-in-submit-button'
import SignInBackButton from './components/sign-in-back-button'
import { getPlanFeatureSettingsPlanFeatureSettingsGetQueryKey } from '@/generated/@tanstack/react-query.gen'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { signIn } from '../../hooks/getAuth'
import { toast } from 'react-toastify'
import { signInInputSchema } from './data/sign-in-schema'
import SignInFooter from './components/sign-in-footer'
import { Link } from 'react-router-dom'

const signInSchema = z.object({
  email: signInInputSchema.shape.email,
  password: signInInputSchema.shape.oldPassword,
})

type SignInFormData = z.infer<typeof signInSchema>

interface Props {
  email: string
  onClickBack: () => void
  onSuccess: ({
    userCredential,
    oldPassword,
  }: {
    userCredential: unknown
    oldPassword: string
  }) => void
}

export default function SignInInputPassword({
  email,
  onClickBack,
  onSuccess,
}: Props) {
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email,
    },
  })

  const password = watch('password')

  const { mutate, isPending } = useMutation({
    mutationFn: (form: SignInFormData) => signIn(form.email, form.password),
    onSuccess: (data) => {
      onSuccess({
        userCredential: data,
        oldPassword: password,
      })
    },
    onError: (error: Error) => toast.warn(error.message),
  })

  const onSubmit = async (data: SignInFormData) => {
    queryClient.resetQueries({
      queryKey: getPlanFeatureSettingsPlanFeatureSettingsGetQueryKey({}),
    })
    mutate(data)
  }

  return (
    <SignInContainer loading={isPending}>
      <SignInFormContainer title="Log In">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex space-y-4 flex-col py-4">
            <div className="mb-2 space-y-4">
              <FormInputPassword
                placeholder="Password"
                register={register('password')}
                error={errors.password}
              />
            </div>
            <SignInSubmitButton text="Log in" />
            <div className="flex justify-end whitespace-nowrap text-sm sm:text-lg">
              <Link
                to="/forgot-password"
                className="text-primary hover:text-blue-800 transition duration-200 ease-in-out ml-1"
              >
                Forgot Password?
              </Link>
            </div>
            <SignInBackButton onClick={onClickBack} />
          </div>
        </form>
      </SignInFormContainer>
      <SignInFooter />
    </SignInContainer>
  )
}
